import { Box, Stack } from '@mui/material'
// @ts-expect-error
import { Video } from 'cloudinary-react'

import cloudinaryRef from '@src/lib/Cloudinary'

import ExternalVideoPreview from '../reporting/ExternalVideoPreview.jsx'
import { SashProps } from '../shared/Sash'

import {
  CampaignCard,
  CampaignCardActionArea,
  CampaignCardActionAreaProps,
  CampaignCardContent,
  CampaignCardHeader,
  CampaignCardHeaderProps,
  CampaignCardMedia,
  CampaignCardProps,
  CampaignCardSecondaryContent,
} from './CampaignCard'

interface OmniCampaignCardMedia {
  publicId?: string | null
  type?: string | null
  externalVideoUrl?: string | null
}

export interface OmniCampaignCardProps {
  title?: CampaignCardHeaderProps['title']
  subheader?: CampaignCardHeaderProps['subheader']

  description?: React.ReactNode
  secondaryContent?: React.ReactNode

  actionAreaLabel: React.ReactNode
  actionAreaColor: CampaignCardActionAreaProps['color']
  actionAreaInvert?: CampaignCardActionAreaProps['invert']

  media: OmniCampaignCardMedia[]

  sashColor?: SashProps['color']
  sashLabel?: SashProps['label']
  sashTooltip?: SashProps['tooltip']

  onClick?: CampaignCardProps['onClick']
  href?: CampaignCardProps['href']
  isLegacyLink?: boolean

  brandLogo?: string | null

  id?: string | number | null

  dim?: CampaignCardProps['dim']
}

const MediaPreview: React.FC<{ media: OmniCampaignCardMedia[] }> = ({
  media,
}) => {
  if (media?.length > 1 && media?.[0]?.publicId && media?.[1]?.publicId) {
    return (
      <Box display={'flex'} flexDirection={'row'}>
        <CampaignCardMedia
          src={cloudinaryRef.instance().url(media?.[0]?.publicId, {
            crop: 'pad',
            width: 400 * 2,
            height: 180 * 2,
            fetch_format: 'jpg',
            quality: 'auto',
          })}
        />
        <Box pl={2}>
          <CampaignCardMedia
            src={cloudinaryRef.instance().url(media?.[1]?.publicId, {
              crop: 'fit',
              width: 100 * 2,
              height: 180 * 2,
              fetch_format: 'jpg',
              quality: 'auto',
            })}
          />
        </Box>
      </Box>
    )
  } else if (media?.[0]?.type === 'video') {
    return media?.[0]?.publicId ? (
      <Video
        autoPlay={false}
        controls={true}
        publicId={media?.[0]?.publicId}
        style={{ width: '100%', height: 180 }}
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation()
        }}
      />
    ) : null
  } else if (media?.[0]?.externalVideoUrl) {
    return (
      <ExternalVideoPreview
        externalUrl={media?.[0]?.externalVideoUrl}
        width={'100%'}
        height={180}
      />
    )
  } else {
    return media?.[0]?.publicId ? (
      <CampaignCardMedia
        src={cloudinaryRef.instance().url(media?.[0]?.publicId, {
          background: 'auto:predominant',
          crop: 'pad',
          width: 500 * 2,
          height: 180 * 2,
          fetch_format: 'jpg',
          quality: 'auto',
        })}
      />
    ) : null
  }
}

export const OmniCampaignCard: React.FC<OmniCampaignCardProps> = (props) => {
  return (
    <CampaignCard
      href={props.href}
      onClick={props.onClick}
      isLegacyLink={props.isLegacyLink}
      dim={props.dim}
      slotProps={{
        sash: props.sashLabel
          ? {
              label: props.sashLabel,
              tooltip: props.sashTooltip,
              color: 'discovery',
            }
          : undefined,
      }}
    >
      <MediaPreview media={props.media} />

      <CampaignCardContent>
        <Stack direction="row" justifyContent="space-between">
          <CampaignCardHeader title={props.title} subheader={props.subheader} />

          {props.brandLogo ? (
            <Box
              height={44}
              flex="0 0 96px"
              sx={{
                '& > img': {
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                },
              }}
            >
              <img src={props.brandLogo || ''} alt="Brand Logo" />
            </Box>
          ) : undefined}
        </Stack>

        {props.description}
      </CampaignCardContent>

      {props.secondaryContent ? (
        <CampaignCardSecondaryContent>
          {props.secondaryContent}
        </CampaignCardSecondaryContent>
      ) : undefined}

      <CampaignCardActionArea
        color={props.actionAreaColor}
        invert={props.actionAreaInvert}
      >
        {props.actionAreaLabel}
      </CampaignCardActionArea>
    </CampaignCard>
  )
}
